// First-party auth client for the /auth/* token-mediating backend (PLATFORM-988, ADR-006 Decision 3).
//
// The refresh token lives only in the HttpOnly `__Host-rt` cookie, which JavaScript can't read.
// Access and ID tokens live only in this module's memory: never in localStorage, sessionStorage,
// a readable cookie or a URL.

import {
  AuthServiceError,
  ChallengeExpiredError,
  InvalidCredentialsError,
  InvalidPasswordError,
  NotSignedInError,
  RefreshUnavailableError,
  TooManyAttemptsError
} from './auth-errors';
import { purgeLegacyAuthState } from './legacy-purge';

export interface Tokens {
  accessToken: string;
  idToken: string;
  /** Epoch milliseconds when the access token expires. */
  expiresAt: number;
  /** Informational only (Cognito, CognitoGoogle, CognitoJumpcloud, Cognito<Provider>). Never trusted for auth. */
  loginMethod: string;
}

/** Minimum access-token life handed to an agent run: 8 h minus 5 min. */
export const AGENT_HANDOFF_MIN_REMAINING_MS = 8 * 60 * 60 * 1000 - 5 * 60 * 1000;

/** Default staleness threshold for getTokens(): refresh when fewer than this much life remains. */
const DEFAULT_MIN_REMAINING_MS = 60_000;

/** Least life a cached token may have to be served when a refresh can't answer. */
export const DEFAULT_STALE_MIN_MS = 10_000;

/** Agent runs: least life for a cached fallback token during a refresh outage. */
export const AGENT_STALE_MIN_MS = 60 * 60 * 1000;

/** 'session_ended': a refresh returned 401 for the current generation. 'signed_out': signOut(). */
export type SignedOutReason = 'session_ended' | 'signed_out';

export {
  AuthServiceError,
  ChallengeExpiredError,
  InvalidCredentialsError,
  InvalidPasswordError,
  NotAuthorizedError,
  NotSignedInError,
  RefreshUnavailableError,
  TooManyAttemptsError
} from './auth-errors';

/** First backoff after a failed (non-401) refresh; doubles per consecutive failure up to the cap. */
const REFRESH_BACKOFF_INITIAL_MS = 1000;
const REFRESH_BACKOFF_MAX_MS = 30_000;

// The entire in-memory session. Module-scoped, never persisted: a reload or new tab starts with
// this null and re-establishes it via bootstrap().
let currentTokens: Tokens | null = null;

// Single-flight: every concurrent getTokens()/bootstrap() call that decides a refresh is needed
// shares this one in-flight POST /auth/refresh instead of firing one each. Safe because both the
// freshness check and the assignment below run synchronously (no `await` between them), so two
// calls made back-to-back synchronously (e.g. Promise.all([getTokens(), getTokens()])) can never
// both observe `refreshInFlight` as null.
let refreshInFlight: Promise<Tokens> | null = null;

// Bumped on every sign-out. A refresh that started under an older generation must not write its
// result back into `currentTokens` (or fire a second onSignedOut): the user signed out while it
// was in flight, and a late 200 must not resurrect the session in memory.
let generation = 0;

// Backoff after a refresh that failed without a definitive answer (5xx, network, malformed 200).
// While now < backoffUntil, getTokens() rejects fast with RefreshUnavailableError and sends no
// request, so a partial /auth/refresh outage isn't amplified by every open tab and caller
// retrying at full speed. A success or a 401 (a definitive answer) resets it.
let backoffUntil = 0;
let backoffMs = 0;

const resetBackoff = (): void => {
  backoffUntil = 0;
  backoffMs = 0;
};

const recordRefreshUnavailable = (): void => {
  backoffMs = backoffMs
    ? Math.min(backoffMs * 2, REFRESH_BACKOFF_MAX_MS)
    : REFRESH_BACKOFF_INITIAL_MS;
  backoffUntil = Date.now() + backoffMs;
};

const signedOutSubscribers = new Set<(reason: SignedOutReason) => void>();

const notifySignedOut = (reason: SignedOutReason): void => {
  currentTokens = null;
  generation += 1;
  refreshInFlight = null;
  resetBackoff();
  for (const cb of signedOutSubscribers) {
    try {
      cb(reason);
    } catch (e) {
      // One subscriber's own error must not stop the others from being notified.
      console.error('auth-client: onSignedOut subscriber threw', e);
    }
  }
};

interface AuthErrorBody {
  error?: string;
  message?: string;
}

// Every POST to /auth/* per the committed HTTP contract: same-origin credentials (carries the
// HttpOnly __Host-rt/__Host-auth_tx/__Host-auth_chal cookies), the X-Auth-Client marker the
// backend requires to tell a real client apart from a bare cross-site POST, and a JSON body.
// timeoutMs aborts a request the server accepted but never answers; the abort rejects like any
// other network failure.
const postAuth = async <T extends object>(
  path: string,
  body: Record<string, unknown> = {},
  timeoutMs?: number
): Promise<{ status: number; json: T & AuthErrorBody }> => {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Auth-Client': '1',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body),
    ...(timeoutMs === undefined ? {} : { signal: AbortSignal.timeout(timeoutMs) })
  });
  let json: T & AuthErrorBody;
  try {
    json = (await res.json()) as T & AuthErrorBody;
  } catch {
    json = {} as T & AuthErrorBody;
  }
  return { status: res.status, json };
};

interface RefreshResponseBody {
  access_token: string;
  id_token: string;
  /** Epoch SECONDS, per the backend contract — converted to ms below. */
  expires_at: number;
  login_method: string;
}

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

// The one validator for every body that carries a session (refresh and both password endpoints):
// a 200 that isn't the documented shape (e.g. an SPA-fallback HTML page) must never become a
// session with undefined tokens.
const parseTokens = (json: Partial<RefreshResponseBody>): Tokens | null => {
  if (
    !isNonEmptyString(json.access_token) ||
    !isNonEmptyString(json.id_token) ||
    typeof json.expires_at !== 'number' ||
    !Number.isFinite(json.expires_at)
  ) {
    return null;
  }
  return {
    accessToken: json.access_token,
    idToken: json.id_token,
    expiresAt: json.expires_at * 1000,
    loginMethod: typeof json.login_method === 'string' ? json.login_method : ''
  };
};

// A new session replaces whatever came before it: bump the generation and drop the in-flight
// refresh so a late answer to a request made for the old session can neither overwrite these
// tokens nor sign the new session out.
const adoptSession = (next: Tokens): void => {
  currentTokens = next;
  generation += 1;
  refreshInFlight = null;
  resetBackoff();
};

// A hung /auth/refresh or /auth/logout is cut off here so a user retry can start a new request
// instead of rejoining the stuck one.
const AUTH_REQUEST_TIMEOUT_MS = 15_000;

const remainingMs = (t: Tokens): number => t.expiresAt - Date.now();

const doRefresh = async (): Promise<Tokens> => {
  const startedInGeneration = generation;
  let status: number;
  let json: Partial<RefreshResponseBody> & AuthErrorBody;
  try {
    const res = await postAuth<RefreshResponseBody>('/auth/refresh', {}, AUTH_REQUEST_TIMEOUT_MS);
    status = res.status;
    // A JSON `null`/scalar body parses fine but has no fields: treat it as an empty object.
    json = res.json && typeof res.json === 'object' ? res.json : {};
  } catch (e) {
    // fetch() itself rejected (network loss, DNS, CORS-less gateway failure): no answer at all.
    // A failure that belongs to a superseded session says nothing about the current one, so it
    // doesn't count against the backoff.
    if (startedInGeneration === generation) {
      recordRefreshUnavailable();
    }
    throw new RefreshUnavailableError('Could not reach the sign-in service.', { cause: e });
  }

  if (startedInGeneration !== generation) {
    // Signed out while this was in flight: whatever came back is stale. Don't repopulate memory,
    // don't clear anything again, and don't count it against the backoff.
    throw new NotSignedInError('Signed out during refresh');
  }

  if (status === 401) {
    resetBackoff();
    notifySignedOut('session_ended');
    throw new NotSignedInError('Not signed in');
  }
  if (status !== 200) {
    recordRefreshUnavailable();
    throw new RefreshUnavailableError(`The sign-in service is unavailable (status ${status}).`);
  }
  const next = parseTokens(json);
  if (!next) {
    recordRefreshUnavailable();
    throw new RefreshUnavailableError('The sign-in service returned an unexpected response.');
  }
  resetBackoff();
  currentTokens = next;
  return next;
};

/**
 * Memory only. Single-flight: parallel callers share one POST /auth/refresh. Refreshes when fewer
 * than minRemainingMs remain (default 60 s) or on forceRefresh. Throws NotSignedInError on 401
 * (the ONLY signal that the session is gone) and RefreshUnavailableError on any other failure,
 * which rejects fast, without a request, while a 1 s-doubling-to-30 s backoff is active.
 *
 * When a refresh can't answer (backoff, or RefreshUnavailableError from this call or a joined
 * in-flight refresh) and forceRefresh is false, returns the cached token if the generation is
 * unchanged since this call started and it has at least `minStaleMs` (default
 * DEFAULT_STALE_MIN_MS) left; otherwise rethrows.
 */
export const getTokens = async (opts?: {
  minRemainingMs?: number;
  forceRefresh?: boolean;
  minStaleMs?: number;
}): Promise<Tokens> => {
  const minRemainingMs = opts?.minRemainingMs ?? DEFAULT_MIN_REMAINING_MS;
  const forceRefresh = opts?.forceRefresh ?? false;
  const minStaleMs = opts?.minStaleMs ?? DEFAULT_STALE_MIN_MS;
  const startedInGeneration = generation;

  if (!forceRefresh && currentTokens && remainingMs(currentTokens) >= minRemainingMs) {
    return currentTokens;
  }

  try {
    if (refreshInFlight) {
      return await refreshInFlight;
    }

    if (Date.now() < backoffUntil) {
      throw new RefreshUnavailableError('The sign-in service is temporarily unavailable.');
    }

    const flight = doRefresh();
    refreshInFlight = flight;
    try {
      return await flight;
    } finally {
      // Only clear our own flight: a sign-out mid-flight already nulled it, and a newer flight may
      // have been started since.
      if (refreshInFlight === flight) {
        refreshInFlight = null;
      }
    }
  } catch (e) {
    // The refresh couldn't answer, which says nothing about the session. A caller that didn't ask
    // for a fresh token can keep working on the cached one while it has enough life left. The
    // generation check keeps a token from a different session (sign-out, then sign-in) from being
    // served to a call that started under the old one.
    if (
      e instanceof RefreshUnavailableError &&
      !forceRefresh &&
      currentTokens &&
      startedInGeneration === generation &&
      remainingMs(currentTokens) >= minStaleMs
    ) {
      return currentTokens;
    }
    throw e;
  }
};

/**
 * On boot, reload or a new tab: purge legacy Amplify state, then one refresh. Returns null when
 * not signed in; throws RefreshUnavailableError when the refresh endpoint couldn't answer.
 */
export const bootstrap = async (): Promise<Tokens | null> => {
  purgeLegacyAuthState();
  try {
    return await getTokens({ forceRefresh: true });
  } catch (e) {
    if (e instanceof NotSignedInError) {
      return null;
    }
    throw e;
  }
};

export const routeEmail = async (
  email: string
): Promise<{ method: 'federated'; provider: string } | { method: 'password' }> => {
  const { status, json } = await postAuth<{ method?: 'federated' | 'password'; provider?: string }>(
    '/auth/route',
    { email }
  );
  if (status !== 200 || (json.method !== 'federated' && json.method !== 'password')) {
    throw new AuthServiceError(`auth-client: unexpected /auth/route response (status ${status})`);
  }
  if (json.method === 'federated') {
    return { method: 'federated', provider: json.provider ?? '' };
  }
  return { method: 'password' };
};

const buildQuery = (params: Record<string, string | undefined>): string => {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      qs.set(key, value);
    }
  }
  return qs.toString();
};

/** Navigates to /auth/login. */
export const beginFederatedLogin = (o: {
  provider?: string;
  loginHint?: string;
  returnTo?: string;
}): void => {
  const query = buildQuery({
    provider: o.provider,
    login_hint: o.loginHint,
    return_to: o.returnTo
  });
  window.location.assign(`/auth/login${query ? `?${query}` : ''}`);
};

interface PasswordSessionBody {
  status?: 'signed_in' | 'new_password_required';
  access_token?: string;
  id_token?: string;
  expires_at?: number;
  login_method?: string;
}

// Stores the session from a `signed_in` body. A malformed body is a service fault, not a sign-in:
// nothing is stored.
const applySignedInBody = (json: PasswordSessionBody, endpoint: string): void => {
  const tokens = parseTokens(json);
  if (!tokens) {
    throw new AuthServiceError(`auth-client: malformed ${endpoint} session response`);
  }
  adoptSession(tokens);
};

export const passwordSignIn = async (
  email: string,
  password: string
): Promise<{ status: 'signed_in' } | { status: 'new_password_required' }> => {
  const { status, json } = await postAuth<PasswordSessionBody>('/auth/password', {
    email,
    password
  });

  if (status === 200 && json.status === 'signed_in') {
    applySignedInBody(json, '/auth/password');
    return { status: 'signed_in' };
  }
  if (status === 200 && json.status === 'new_password_required') {
    return { status: 'new_password_required' };
  }
  if (status === 401 && json.error === 'invalid_credentials') {
    throw new InvalidCredentialsError('Incorrect email or password.');
  }
  if (status === 429) {
    throw new TooManyAttemptsError(
      'Too many sign-in attempts. Please wait a moment and try again.'
    );
  }
  throw new AuthServiceError(`auth-client: unexpected /auth/password response (status ${status})`);
};

export const completeNewPassword = async (newPassword: string): Promise<void> => {
  const { status, json } = await postAuth<PasswordSessionBody>('/auth/new-password', {
    new_password: newPassword
  });

  if (status === 200 && json.status === 'signed_in') {
    applySignedInBody(json, '/auth/new-password');
    return;
  }
  if (status === 400 && json.error === 'invalid_password') {
    throw new InvalidPasswordError(json.message ?? 'That password does not meet the policy.');
  }
  if (status === 401 && json.error === 'challenge_expired') {
    throw new ChallengeExpiredError('That sign-in attempt expired. Please sign in again.');
  }
  throw new AuthServiceError(
    `auth-client: unexpected /auth/new-password response (status ${status})`
  );
};

// The backend only ever returns the Cognito hosted-UI logout endpoint on its prefix domain
// (<prefix>.auth.<region>.amazoncognito.com). Anything else is ignored rather than navigated to.
const COGNITO_LOGOUT_HOST_RE = /^[a-z0-9-]+\.auth\.[a-z0-9-]+\.amazoncognito\.com$/;

const parseLogoutUrl = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.length === 0) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const isCognitoLogout =
    url.protocol === 'https:' &&
    url.port === '' &&
    COGNITO_LOGOUT_HOST_RE.test(url.hostname) &&
    url.pathname === '/logout';
  return isCognitoLogout ? url.href : null;
};

/**
 * POST /auth/logout, clears memory, then navigates to logout_url when it's present. Resolves true
 * when it navigated away (a federated session), so the caller can leave the page as it is rather
 * than render a signed-out view the navigation is about to replace.
 */
export const signOut = async (): Promise<boolean> => {
  let logoutUrl: string | null = null;
  try {
    const { json } = await postAuth<{ logout_url?: unknown }>(
      '/auth/logout',
      {},
      AUTH_REQUEST_TIMEOUT_MS
    );
    logoutUrl = parseLogoutUrl(json.logout_url);
    if (logoutUrl === null && json.logout_url) {
      console.error('auth-client: ignoring an unexpected logout_url from /auth/logout');
    }
  } catch (e) {
    // The backend contract says this endpoint always 200s, but the fetch itself can still fail
    // (network blip). Never strand the user mid-logout — fall through and clear local state
    // regardless.
    console.error('auth-client: /auth/logout request failed', e);
  }
  notifySignedOut('signed_out');
  if (logoutUrl) {
    window.location.assign(logoutUrl);
    return true;
  }
  return false;
};

/** Subscribes to sign-out (logout, or a refresh that returns 401). Returns the unsubscribe function. */
export const onSignedOut = (cb: (reason: SignedOutReason) => void): (() => void) => {
  signedOutSubscribers.add(cb);
  return () => {
    signedOutSubscribers.delete(cb);
  };
};

/** Clears the refresh backoff. For user-initiated retries only. */
export const resetRefreshBackoff = (): void => {
  resetBackoff();
};

const decodeJwtPayload = (jwt: string): unknown => {
  const segment = jwt.split('.')[1];
  if (!segment) return null;
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
};

/** The access token's issued lifetime in ms (exp − iat), or null if it can't be read. */
export const tokenLifetimeMs = (t: Tokens): number | null => {
  try {
    const payload = decodeJwtPayload(t.accessToken);
    if (typeof payload !== 'object' || payload === null) return null;
    if (!('exp' in payload) || !('iat' in payload)) return null;
    const { exp, iat } = payload;
    if (typeof exp !== 'number' || typeof iat !== 'number') return null;
    const lifetimeMs = (exp - iat) * 1000;
    return Number.isFinite(lifetimeMs) && lifetimeMs > 0 ? lifetimeMs : null;
  } catch {
    return null;
  }
};
